import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

// ─── Apps Script endpoint (replace with your deployed Web App URL) ────────────
const BLOG_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyWov8e2rPmKMTtyj3BSrI9kYrPAJtg3MeInPuDGE9aWCaJ0keoqJ17DMx5_PbHN-mA/exec';

// ─── Types ───────────────────────────────────────────────────────────────────
interface BlogPostData {
  id: number;
  title: string;
  date: string;
  excerpt: string;
  content: string;
  author: string;
  authorAvatar: string;
  readTime: string;
  category: string;
  featured?: boolean;
  gradient: string;
}

// ─── Category Config ──────────────────────────────────────────────────────────
const CATEGORIES = ["All Posts", "Career Guidance", "Interview Tips", "Tech Insights", "Industry News"];

const CATEGORY_STYLES: Record<string, { pill: string; gradient: string; accent: string }> = {
  "Career Guidance": { pill: "bg-violet-100 text-violet-700", gradient: "from-violet-600 to-primary", accent: "#7c3aed" },
  "Interview Tips":  { pill: "bg-sky-100 text-sky-700",      gradient: "from-sky-500 to-secondary",  accent: "#0ea5e9" },
  "Tech Insights":   { pill: "bg-emerald-100 text-emerald-700", gradient: "from-emerald-500 to-teal-600", accent: "#10b981" },
  "Industry News":   { pill: "bg-orange-100 text-orange-600", gradient: "from-orange-500 to-accent",  accent: "#FF6535" },
};

const getCategoryStyle = (cat: string) =>
  CATEGORY_STYLES[cat] ?? { pill: "bg-gray-100 text-gray-600", gradient: "from-primary to-secondary", accent: "#2b2240" };

// ─── Initial Blog Data ────────────────────────────────────────────────────────
const INITIAL_POSTS: BlogPostData[] = [
  {
    id: 1,
    title: "Building a Winning Tech Resume",
    date: "April 17, 2025",
    excerpt:
      "Your resume is the first impression a hiring manager will have of you. A strong resume doesn't just list experiences — it tells the story of your skills, achievements, and the value you bring.",
    content: `Your resume is the gateway to every opportunity in tech. Here's how to make it count.

## Start with a Clear Structure
Use section headers like Contact Information, Skills, Experience, Education, and Projects. Keep formatting clean with bullet points so recruiters can scan it in seconds.

## Tailor Your Resume to the Job
Use the job description as a guide and match your skills to the requirements. If the role requires Spring Boot or Angular — highlight them prominently.

## Highlight Technical Skills
List both technical (Java, Python, JavaScript) and soft skills (communication, teamwork). Consider two distinct sections so hiring managers can quickly identify your abilities.

## Showcase Projects and Achievements
Projects matter — especially for early-career professionals. Include tools used, challenges overcome, and outcomes. Link to your GitHub or portfolio.

## Quantify Your Impact
Numbers speak louder than words. "Increased API performance by 30%" beats "improved performance." Show the value you delivered, not just what you did.`,
    author: "Vista Tech Team",
    authorAvatar: "V",
    readTime: "5 min read",
    category: "Career Guidance",
    featured: true,
    gradient: "from-violet-600 to-primary",
  },
  {
    id: 2,
    title: "Mastering the Tech Interview Process",
    date: "April 17, 2025",
    excerpt:
      "Once your resume catches the recruiter's eye, the interview is your stage. Here's how to prepare for both the technical and behavioral rounds — and walk in with confidence.",
    content: `The interview is where careers are made. Prepare thoroughly and walk in with confidence.

## Technical Interview Prep
Practice coding problems on LeetCode, HackerRank, or CodeSignal. Focus on algorithms, data structures, and breaking down complex problems — interviewers want to see *how* you think, not just whether you get the right answer.

## Behavioral Interview Prep
Use the STAR method (Situation, Task, Action, Result) to answer experience-based questions. Craft 3–5 strong stories that demonstrate leadership, problem-solving, and collaboration.

## Research the Company and Role
Study their products, recent news, and tech stack. Being able to connect your experience to their challenges signals genuine interest and preparation.

## Prepare Smart Questions
Ask about team dynamics, the tech stack, or growth plans. Thoughtful questions distinguish you from other candidates and show you're evaluating them too.

## Key Takeaways
- Practice coding daily for 2–3 weeks before interviews
- Prepare STAR stories for common behavioral questions
- Research the company deeply
- Ask questions that show you're thinking long-term`,
    author: "Vista Tech Team",
    authorAvatar: "V",
    readTime: "4 min read",
    category: "Interview Tips",
    gradient: "from-sky-500 to-secondary",
  },
];

// ─── Floating Particle ────────────────────────────────────────────────────────
const Particle = ({ style }: { style: React.CSSProperties }) => (
  <motion.div
    className="absolute rounded-full opacity-20 pointer-events-none"
    style={style}
    animate={{ y: [-20, 20, -20], x: [-10, 10, -10], scale: [1, 1.2, 1] }}
    transition={{ duration: 6 + Math.random() * 4, repeat: Infinity, ease: "easeInOut" }}
  />
);

// ─── Render Content ───────────────────────────────────────────────────────────
const RenderContent = ({ text }: { text: string }) => {
  const blocks = text.trim().split('\n\n');
  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        if (block.startsWith('## ')) {
          return (
            <h3 key={i} className="text-xl font-bold text-primary mt-6 mb-2 flex items-center gap-2">
              <span className="w-1 h-5 bg-accent rounded-full inline-block" />
              {block.replace('## ', '')}
            </h3>
          );
        }
        if (block.includes('\n- ')) {
          const [intro, ...items] = block.split('\n');
          return (
            <div key={i}>
              {intro && <p className="text-gray-700 leading-relaxed mb-2">{intro}</p>}
              <ul className="space-y-1">
                {items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-gray-700">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                    {item.replace('- ', '')}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return (
          <p key={i} className="text-gray-700 leading-relaxed">
            {block}
          </p>
        );
      })}
    </div>
  );
};

// ─── Blog Modal ───────────────────────────────────────────────────────────────
const BlogModal = ({ blog, onClose }: { blog: BlogPostData; onClose: () => void }) => {
  const style = getCategoryStyle(blog.category);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-dark/70 backdrop-blur-sm" />

      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 60, opacity: 0, scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden mb-8"
        onClick={e => e.stopPropagation()}
      >
        {/* Gradient header bar */}
        <div className={`h-2 w-full bg-gradient-to-r ${style.gradient}`} />

        <div className="p-8 md:p-10">
          {/* Meta */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1 pr-6">
              <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${style.pill} mb-3`}>
                {blog.category}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-primary leading-tight">{blog.title}</h2>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Author row */}
          <div className="flex items-center gap-4 pb-6 border-b border-gray-100 mb-6">
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${style.gradient} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
              {blog.authorAvatar}
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{blog.author}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{blog.date}</span>
                <span>·</span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6l4 2" />
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                </svg>
                <span>{blog.readTime}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <RenderContent text={blog.content} />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Featured Card ────────────────────────────────────────────────────────────
const FeaturedCard = ({ post, onClick }: { post: BlogPostData; onClick: () => void }) => {
  const style = getCategoryStyle(post.category);
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="group col-span-full"
    >
      <div
        className="relative rounded-3xl overflow-hidden cursor-pointer"
        style={{ background: "linear-gradient(135deg, #2b2240 0%, #4e3a69 50%, #2b2240 100%)" }}
        onClick={onClick}
      >
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" />

        <div className="relative p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="bg-accent/20 text-accent border border-accent/30 text-xs font-bold px-3 py-1 rounded-full">
                ★ Featured
              </span>
              <span className={`text-xs font-bold px-3 py-1 rounded-full bg-white/10 text-white`}>
                {post.category}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4 group-hover:text-accent transition-colors duration-300">
              {post.title}
            </h2>
            <p className="text-gray-300 leading-relaxed mb-6 line-clamp-3">
              {post.excerpt}
            </p>
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${style.gradient} flex items-center justify-center text-white font-bold text-sm`}>
                {post.authorAvatar}
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{post.author}</p>
                <p className="text-gray-400 text-xs">{post.date} · {post.readTime}</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center md:justify-end">
            <motion.div
              whileHover={{ scale: 1.05, rotate: 2 }}
              className={`w-48 h-48 md:w-56 md:h-56 rounded-2xl bg-gradient-to-br ${style.gradient} flex flex-col items-center justify-center p-6 shadow-2xl`}
            >
              <svg className="w-16 h-16 text-white/80 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span className="text-white font-bold text-sm text-center">Read Article</span>
            </motion.div>
          </div>
        </div>

        {/* Read more CTA */}
        <div className={`bg-gradient-to-r ${style.gradient} px-8 md:px-12 py-3 flex items-center justify-between`}>
          <span className="text-white/80 text-sm">Click to read the full article</span>
          <motion.div
            className="flex items-center gap-1 text-white font-semibold text-sm"
            whileHover={{ x: 4 }}
          >
            Read More
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.div>
        </div>
      </div>
    </motion.article>
  );
};

// ─── Blog Card ────────────────────────────────────────────────────────────────
const BlogCard = ({ post, index, onClick }: { post: BlogPostData; index: number; onClick: () => void }) => {
  const style = getCategoryStyle(post.category);
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.12, duration: 0.5 }}
      className="group cursor-pointer"
      onClick={onClick}
    >
      <div className="h-full bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-400 overflow-hidden flex flex-col border border-gray-100 hover:-translate-y-1">
        {/* Top gradient accent */}
        <div className={`h-1.5 w-full bg-gradient-to-r ${style.gradient}`} />

        <div className="p-6 flex flex-col flex-1">
          {/* Category + Date */}
          <div className="flex items-center justify-between mb-4">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${style.pill}`}>
              {post.category}
            </span>
            <span className="text-xs text-gray-400">{post.date}</span>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-primary mb-3 leading-tight group-hover:text-secondary transition-colors duration-300 line-clamp-2">
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1 line-clamp-3">
            {post.excerpt}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-50">
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${style.gradient} flex items-center justify-center text-white font-bold text-xs`}>
                {post.authorAvatar}
              </div>
              <div>
                <p className="text-gray-700 text-xs font-semibold">{post.author}</p>
                <p className="text-gray-400 text-xs">{post.readTime}</p>
              </div>
            </div>
            <motion.div
              className="flex items-center gap-1 text-secondary text-sm font-semibold"
              whileHover={{ x: 3 }}
            >
              Read
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.article>
  );
};


// ─── Main Blog Page ───────────────────────────────────────────────────────────
const Blog = () => {
  const [posts, setPosts] = useState<BlogPostData[]>(INITIAL_POSTS);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState('All Posts');
  const heroRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const fetchPosts = () => {
    setLoading(true);
    fetch(BLOG_SCRIPT_URL)
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.posts) && data.posts.length > 0) {
          setPosts(data.posts.map((p: any) => ({
            ...p,
            id: Number(p.id),
            featured: p.featured === true || p.featured === 'TRUE' || p.featured === 'true',
          })));
        }
      })
      .catch(() => {/* fallback: keep INITIAL_POSTS */})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPosts();
    // Re-sync whenever user returns to this tab (admin may have published)
    const onFocus = () => fetchPosts();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPost = posts.find(p => p.id === selectedId);

  const filteredPosts = activeCategory === 'All Posts'
    ? posts
    : posts.filter(p => p.category === activeCategory);

  const featuredPost = filteredPosts.find(p => p.featured) ?? filteredPosts[0];
  const regularPosts = filteredPosts.filter(p => p.id !== featuredPost?.id);

  // Particles config (stable — no re-render)
  const particles = [
    { width: 80, height: 80, background: '#F0B429', top: '15%', left: '8%', borderRadius: '50%' },
    { width: 50, height: 50, background: '#FF6535', top: '70%', left: '5%', borderRadius: '50%' },
    { width: 100, height: 100, background: '#4e3a69', top: '20%', right: '6%', borderRadius: '50%' },
    { width: 60, height: 60, background: '#F0B429', top: '60%', right: '10%', borderRadius: '50%' },
    { width: 40, height: 40, background: '#FF6535', top: '40%', left: '50%', borderRadius: '50%' },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">

      {/* ── Hero ── */}
      <div ref={heroRef} className="relative overflow-hidden bg-gradient-to-br from-dark via-primary to-secondary min-h-[420px] flex items-center">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="w-full">
          {particles.map((p, i) => <Particle key={i} style={p} />)}

          <div className="container mx-auto px-6 py-20 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <motion.span
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-block bg-white/10 text-accent border border-accent/30 text-xs font-bold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase"
              >
                Vista Tech Blog
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="text-5xl md:text-7xl font-bold text-white leading-tight mb-5"
              >
                Ideas &{' '}
                <span className="bg-gradient-to-r from-accent to-orange bg-clip-text text-transparent">
                  Insights
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-gray-300 text-lg max-w-2xl mx-auto"
              >
                Expert guides on tech careers, interview prep, industry trends, and everything
                that matters for your professional journey.
              </motion.p>

              {/* Stats row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex items-center justify-center gap-8 mt-10"
              >
                {[
                  { label: 'Articles', value: posts.length },
                  { label: 'Categories', value: CATEGORIES.length - 1 },
                  { label: 'Avg Read', value: '4 min' },
                ].map(stat => (
                  <div key={stat.label} className="text-center">
                    <p className="text-2xl font-bold text-accent">{stat.value}</p>
                    <p className="text-gray-400 text-xs uppercase tracking-wide">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>

          {/* Wave divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 60L1440 60L1440 20C1200 60 960 0 720 20C480 40 240 0 0 20L0 60Z" fill="#f5f5f5" />
            </svg>
          </div>
        </motion.div>
      </div>

      {/* ── Content ── */}
      <div className="container mx-auto px-4 md:px-6 py-12">

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-2 justify-center mb-12"
        >
          {CATEGORIES.map(cat => {
            const active = cat === activeCategory;
            return (
              <motion.button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  active
                    ? 'bg-primary text-white shadow-lg shadow-primary/30'
                    : 'bg-white text-gray-600 hover:text-primary border border-gray-200 hover:border-primary/30'
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="activeCat"
                    className="absolute inset-0 bg-primary rounded-full -z-10"
                  />
                )}
                {cat}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Blog Grid */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
                  <div className="h-1.5 bg-gray-200 w-full" />
                  <div className="p-6 space-y-3">
                    <div className="flex justify-between">
                      <div className="h-5 w-24 bg-gray-100 rounded-full" />
                      <div className="h-4 w-20 bg-gray-100 rounded-full" />
                    </div>
                    <div className="h-6 w-3/4 bg-gray-200 rounded-lg" />
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-100 rounded w-full" />
                      <div className="h-4 bg-gray-100 rounded w-5/6" />
                      <div className="h-4 bg-gray-100 rounded w-4/6" />
                    </div>
                    <div className="flex justify-between pt-3 border-t border-gray-50">
                      <div className="flex gap-2 items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200" />
                        <div className="h-4 w-20 bg-gray-100 rounded" />
                      </div>
                      <div className="h-4 w-14 bg-gray-100 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          ) : filteredPosts.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20 text-gray-400"
            >
              <svg className="w-16 h-16 mx-auto mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">No posts in this category yet.</p>
              <p className="text-sm mt-1">Check back soon — our team is working on it!</p>
            </motion.div>
          ) : (
            <motion.div key={activeCategory} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Featured */}
              {featuredPost && (
                <div className="mb-8">
                  <FeaturedCard post={featuredPost} onClick={() => setSelectedId(featuredPost.id)} />
                </div>
              )}

              {/* Regular grid */}
              {regularPosts.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularPosts.map((post, i) => (
                    <BlogCard key={post.id} post={post} index={i} onClick={() => setSelectedId(post.id)} />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedPost && (
          <BlogModal blog={selectedPost} onClose={() => setSelectedId(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Blog;
